import { useEffect, useMemo, useRef, useState } from "react";
import {
  Archive,
  ArrowDownAZ,
  ArrowDownUp,
  ArrowUpFromLine,
  ChevronRight,
  Download,
  File as FileIcon,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Folder,
  FolderOpen,
  MoreVertical,
  Move,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { FormActions, Modal } from "../components/Modal";
import { EmptyState } from "../components/Surface";
import { useProjectFileManager } from "../data/useProjectFileManager";
import {
  breadcrumbs,
  canManageFile,
  canManageFolder,
  currentFolderItems,
  fileKind,
  fileTypeLabel,
  folderMoveTargets,
  searchProjectFiles,
  sortManagerItems,
  type ArchiveFilter,
  type FileSortKey,
  type ManagerItem,
  type SortDirection,
} from "../lib/file-manager";
import { PROJECT_FILE_EXTENSIONS, validateProjectFile } from "../lib/files";
import { formatBytes, formatLongDate } from "../lib/format";
import type { ManagedFileRecord, ProjectFolder } from "../types";

type Action =
  | { type: "new-folder" }
  | { type: "rename-folder"; folder: ProjectFolder }
  | { type: "move-folder"; folder: ProjectFolder }
  | { type: "archive-folder"; folder: ProjectFolder; archived: boolean }
  | { type: "delete-folder"; folder: ProjectFolder }
  | { type: "rename-file"; file: ManagedFileRecord }
  | { type: "move-file"; file: ManagedFileRecord }
  | { type: "archive-file"; file: ManagedFileRecord; archived: boolean }
  | { type: "delete-file"; file: ManagedFileRecord };

interface UploadEntry {
  id: string;
  name: string;
  status: "pending" | "uploading" | "done" | "error";
  message: string;
}

const actionTitle: Record<Action["type"], string> = {
  "new-folder": "Nueva carpeta",
  "rename-folder": "Renombrar carpeta",
  "move-folder": "Mover carpeta",
  "archive-folder": "Actualizar carpeta",
  "delete-folder": "Eliminar carpeta",
  "rename-file": "Renombrar archivo",
  "move-file": "Mover archivo",
  "archive-file": "Actualizar archivo",
  "delete-file": "Eliminar archivo",
};

function actionSubmitLabel(action: Action) {
  if (action.type === "delete-folder" || action.type === "delete-file") return "Eliminar";
  if (action.type === "archive-folder" || action.type === "archive-file") return action.archived ? "Archivar" : "Restaurar";
  if (action.type === "move-folder" || action.type === "move-file") return "Mover";
  return action.type === "new-folder" ? "Crear carpeta" : "Guardar cambios";
}

function ItemIcon({ item }: { item: ManagerItem }) {
  if (item.kind === "folder") return <Folder size={21} />;
  const kind = fileKind(item.file.name, item.file.mime_type);
  if (kind === "pdf" || kind === "document" || kind === "presentation")
    return <FileText size={21} />;
  if (kind === "spreadsheet") return <FileSpreadsheet size={21} />;
  if (kind === "image") return <FileImage size={21} />;
  if (kind === "audio") return <FileAudio size={21} />;
  if (kind === "video") return <FileVideo size={21} />;
  return <FileIcon size={21} />;
}

export function FilesPage() {
  const manager = useProjectFileManager();
  const input = useRef<HTMLInputElement>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ArchiveFilter>("active");
  const [sortKey, setSortKey] = useState<FileSortKey>("name");
  const [direction, setDirection] = useState<SortDirection>("asc");
  const [dragging, setDragging] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [uploads, setUploads] = useState<UploadEntry[]>([]);
  const snapshot = manager.snapshot;

  useEffect(() => {
    if (currentFolderId && snapshot && !snapshot.folders.some((folder) => folder.id === currentFolderId))
      setCurrentFolderId(null);
  }, [currentFolderId, snapshot]);

  const trail = useMemo(
    () => (snapshot ? breadcrumbs(snapshot.folders, currentFolderId) : []),
    [currentFolderId, snapshot],
  );
  const searchResults = useMemo(
    () => (snapshot ? searchProjectFiles(snapshot, query, filter) : []),
    [filter, query, snapshot],
  );
  const items = useMemo(() => {
    if (!snapshot) return [];
    const source = query.trim()
      ? searchResults.map((result) => result.item)
      : currentFolderItems(snapshot, currentFolderId, filter);
    return sortManagerItems(source, sortKey, direction);
  }, [currentFolderId, direction, filter, query, searchResults, snapshot, sortKey]);

  const navigate = (folderId: string | null) => {
    setCurrentFolderId(folderId);
    setQuery("");
    setOpenMenu(null);
  };

  const uploadMany = async (selected: File[]) => {
    if (!selected.length) return;
    const queued = selected.map((file) => {
      const validation = validateProjectFile(file);
      const id = crypto.randomUUID();
      return {
        id,
        file,
        valid: validation.ok,
        entry: {
          id,
          name: file.name,
          status: validation.ok ? "pending" : "error",
          message: validation.ok ? "En cola" : validation.message,
        } satisfies UploadEntry,
      };
    });
    setUploads((current) => [
      ...queued.map((item) => item.entry),
      ...current.filter((item) => item.status === "uploading"),
    ]);
    for (const queuedFile of queued) {
      if (!queuedFile.valid) continue;
      setUploads((current) => current.map((item) => item.id === queuedFile.id ? { ...item, status: "uploading", message: "Subiendo…" } : item));
      const ok = await manager.upload(queuedFile.file, currentFolderId);
      setUploads((current) => current.map((item) => item.id === queuedFile.id ? { ...item, status: ok ? "done" : "error", message: ok ? "Listo" : "No se pudo subir" } : item));
    }
    if (input.current) input.current.value = "";
  };

  const openItem = (item: ManagerItem) => {
    if (item.kind === "folder") navigate(item.folder.id);
    else if (query.trim()) navigate(item.file.folder_id);
    else void manager.download(item.file);
  };

  const runAction = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!action || !snapshot) return;
    const values = new FormData(event.currentTarget);
    let ok = false;
    if (action.type === "new-folder") ok = await manager.createFolder(currentFolderId, String(values.get("name") ?? ""));
    if (action.type === "rename-folder") ok = await manager.renameFolder(action.folder, String(values.get("name") ?? ""));
    if (action.type === "move-folder") ok = await manager.moveFolder(action.folder.id, String(values.get("folder_id") ?? "") || null);
    if (action.type === "archive-folder") ok = await manager.setFolderArchived(action.folder.id, action.archived);
    if (action.type === "delete-folder") ok = await manager.deleteFolder(action.folder.id);
    if (action.type === "rename-file") ok = await manager.renameFile(action.file.id, String(values.get("name") ?? ""));
    if (action.type === "move-file") ok = await manager.moveFile(action.file.id, String(values.get("folder_id") ?? "") || null);
    if (action.type === "archive-file") ok = await manager.setFileArchived(action.file.id, action.archived);
    if (action.type === "delete-file") ok = await manager.deleteFile(action.file.id);
    if (ok) setAction(null);
  };

  if (manager.loading && !snapshot)
    return <div className="page-loading"><span /><p>Abriendo archivos…</p></div>;
  if (!snapshot)
    return <div className="page-error"><h1>No pudimos abrir Archivos</h1><p>{manager.error}</p><button className="button primary" onClick={() => void manager.reload()}>Reintentar</button></div>;

  const folderOptions = snapshot.folders.filter((folder) => !folder.effective_archived);
  const moveOptions = action?.type === "move-folder"
    ? folderMoveTargets(snapshot.folders, action.folder.id)
    : folderOptions;

  return <div className="files-page file-manager-page page-enter">
    <header className="page-heading file-manager-heading">
      <div><span className="eyebrow">BIBLIOTECA DEL PROYECTO</span><h1>Archivos</h1><p>Carpetas y documentos privados del proyecto, organizados en un solo lugar.</p></div>
      <div className="file-manager-heading-actions"><button className="button secondary" onClick={() => setAction({ type: "new-folder" })}><Plus size={18}/>Nueva carpeta</button><button className="button primary" onClick={() => input.current?.click()}><ArrowUpFromLine size={18}/>Subir archivos</button></div>
    </header>

    {manager.error && <div className="file-manager-alert" role="alert"><span>{manager.error}</span><button aria-label="Cerrar aviso" onClick={manager.clearError}><X size={16}/></button></div>}

    <nav className="file-breadcrumbs" aria-label="Ruta de carpetas"><button onClick={() => navigate(null)}>Archivos</button>{trail.map((folder) => <span key={folder.id}><ChevronRight size={14}/><button onClick={() => navigate(folder.id)}>{folder.name}</button></span>)}</nav>

    <div className="file-manager-toolbar">
      <label className="search-control"><Search size={18}/><input aria-label="Buscar archivos y carpetas" placeholder="Buscar en todo el proyecto…" value={query} onChange={(event) => setQuery(event.target.value)}/></label>
      <label className="filter-control"><span>Estado</span><select value={filter} onChange={(event) => setFilter(event.target.value as ArchiveFilter)}><option value="active">Activos</option><option value="archived">Archivados</option><option value="all">Todos</option></select></label>
      <label className="filter-control"><ArrowDownAZ size={16}/><span>Ordenar</span><select value={sortKey} onChange={(event) => setSortKey(event.target.value as FileSortKey)}><option value="name">Nombre</option><option value="date">Fecha</option><option value="type">Tipo</option><option value="size">Tamaño</option></select></label>
      <button className="icon-button sort-direction" aria-label={direction === "asc" ? "Orden ascendente" : "Orden descendente"} title={direction === "asc" ? "Ascendente" : "Descendente"} onClick={() => setDirection((current) => current === "asc" ? "desc" : "asc")}><ArrowDownUp size={17}/></button>
    </div>

    <section className={`file-drop-target ${dragging ? "dragging" : ""}`} onDragOver={(event) => { event.preventDefault(); setDragging(true); }} onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false); }} onDrop={(event) => { event.preventDefault(); setDragging(false); void uploadMany(Array.from(event.dataTransfer.files)); }}>
      <input ref={input} hidden multiple type="file" accept={PROJECT_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(",")} onChange={(event) => void uploadMany(Array.from(event.target.files ?? []))}/>
      <ArrowUpFromLine size={18}/><span>Arrastra archivos aquí para subirlos a <b>{trail.at(-1)?.name ?? "Archivos"}</b></span><small>PDF, Office, imágenes, audio y video · máximo 50 MB por archivo</small>
    </section>

    {uploads.length > 0 && <section className="upload-queue" aria-label="Estado de las subidas"><header><strong>Subidas</strong><button onClick={() => setUploads((current) => current.filter((item) => item.status === "uploading" || item.status === "pending"))}>Limpiar terminadas</button></header>{uploads.map((item) => <div key={item.id} className={`upload-entry ${item.status}`}><FileIcon size={16}/><span>{item.name}</span><small>{item.message}</small></div>)}</section>}

    <section className="file-manager-surface">
      <header><div><h2>{query.trim() ? "Resultados de búsqueda" : trail.at(-1)?.name ?? "Archivos"}</h2><span>{items.length} elementos</span></div>{query.trim() && <small>La búsqueda incluye todo el proyecto.</small>}</header>
      {items.length > 0 ? <div className="file-manager-table">
        <div className="file-manager-table-head"><span>Nombre</span><span>Tipo</span><span>Tamaño</span><span>Modificado</span><span>Subido por</span><span>Acciones</span></div>
        {items.map((item) => {
          const id = item.kind === "folder" ? item.folder.id : item.file.id;
          const result = query.trim() ? searchResults.find((candidate) => candidate.item.kind === item.kind && (candidate.item.kind === "folder" ? candidate.item.folder.id : candidate.item.file.id) === id) : undefined;
          const canManage = item.kind === "folder" ? canManageFolder(snapshot, item.folder) : canManageFile(snapshot.viewer_role, snapshot.viewer_id, item.file);
          const ready = item.kind === "folder" || item.file.upload_status === "READY";
          return <article key={`${item.kind}-${id}`} className={item.kind === "folder" ? "folder-row" : "file-row"}>
            <button className="manager-name-cell" onDoubleClick={() => openItem(item)} onClick={() => item.kind === "folder" ? openItem(item) : undefined}><span className={`manager-type-icon ${item.kind === "file" ? fileKind(item.file.name, item.file.mime_type) : "folder"}`}><ItemIcon item={item}/></span><span><strong>{item.kind === "folder" ? item.folder.name : item.file.name}</strong><small>{result?.location ?? (item.kind === "file" ? item.file.description || item.file.category : item.folder.archived_at ? "Carpeta archivada" : "Carpeta")}</small></span></button>
            <span>{item.kind === "folder" ? "Carpeta" : fileTypeLabel(item.file)}{!ready && item.kind === "file" && <small className="lifecycle-label">{item.file.upload_status === "PENDING" ? "Pendiente" : "Eliminando"}</small>}</span>
            <span>{item.kind === "folder" ? "—" : formatBytes(item.file.size_bytes)}</span>
            <time>{formatLongDate(item.kind === "folder" ? item.folder.updated_at : item.file.updated_at)}</time>
            <span>{item.kind === "folder" ? item.folder.creator_name : item.file.uploader_name}</span>
            <div className="manager-actions"><button className="icon-button" aria-label={`Opciones de ${item.kind === "folder" ? item.folder.name : item.file.name}`} onClick={() => setOpenMenu((current) => current === id ? null : id)}><MoreVertical size={18}/></button>{openMenu === id && <div className="manager-menu">
              <button onClick={() => openItem(item)}>{item.kind === "folder" ? <FolderOpen/> : <Download/>}{item.kind === "folder" ? "Abrir" : "Descargar"}</button>
              {canManage && ready && <><button onClick={() => { setAction(item.kind === "folder" ? { type: "rename-folder", folder: item.folder } : { type: "rename-file", file: item.file }); setOpenMenu(null); }}><Pencil/>Renombrar</button><button onClick={() => { setAction(item.kind === "folder" ? { type: "move-folder", folder: item.folder } : { type: "move-file", file: item.file }); setOpenMenu(null); }}><Move/>Mover a…</button>
                {item.kind === "folder" ? <button onClick={() => { setAction({ type: "archive-folder", folder: item.folder, archived: !item.folder.archived_at }); setOpenMenu(null); }}>{item.folder.archived_at ? <RotateCcw/> : <Archive/>}{item.folder.archived_at ? "Restaurar" : "Archivar"}</button> : <button onClick={() => { setAction({ type: "archive-file", file: item.file, archived: !item.file.archived_at }); setOpenMenu(null); }}>{item.file.archived_at ? <RotateCcw/> : <Archive/>}{item.file.archived_at ? "Restaurar" : "Archivar"}</button>}
                <button className="danger" onClick={() => { setAction(item.kind === "folder" ? { type: "delete-folder", folder: item.folder } : { type: "delete-file", file: item.file }); setOpenMenu(null); }}><Trash2/>Eliminar</button></>}
            </div>}</div>
          </article>;
        })}
      </div> : <EmptyState icon={currentFolderId ? <FolderOpen/> : <Folder/>} title={query.trim() ? "No encontramos resultados" : "Esta carpeta está vacía."} text={query.trim() ? "Prueba otro nombre o cambia el filtro de estado." : "Crea una carpeta o sube archivos para empezar a organizar el proyecto."} action={!query.trim() ? <div className="empty-file-actions"><button className="button secondary" onClick={() => setAction({ type: "new-folder" })}>Crear carpeta</button><button className="button primary" onClick={() => input.current?.click()}>Subir archivos</button></div> : undefined}/>}
    </section>

    <Modal open={Boolean(action)} title={action ? actionTitle[action.type] : "Archivos"} onClose={() => setAction(null)}>{action && <form className="form-grid" onSubmit={runAction}>
      {(action.type === "new-folder" || action.type === "rename-folder" || action.type === "rename-file") && <label className="full">{action.type === "rename-file" ? "Nombre del archivo" : "Nombre de carpeta"}<input autoFocus name="name" required maxLength={action.type === "rename-file" ? 255 : 120} defaultValue={action.type === "rename-folder" ? action.folder.name : action.type === "rename-file" ? action.file.name : ""}/>{action.type === "rename-file" && <small className="form-help">Conserva la extensión original.</small>}</label>}
      {(action.type === "move-folder" || action.type === "move-file") && <label className="full">Carpeta destino<select name="folder_id" defaultValue={action.type === "move-folder" ? action.folder.parent_folder_id ?? "" : action.file.folder_id ?? ""}><option value="">Archivos (raíz)</option>{moveOptions.map((folder) => <option key={folder.id} value={folder.id}>{`${"— ".repeat(Math.max(0, folder.name_path.length - 1))}${folder.name}`}</option>)}</select></label>}
      {(action.type === "archive-folder" || action.type === "archive-file") && <div className="confirm-dialog full"><p>{action.archived ? "El elemento dejará de aparecer en Activos. Si es una carpeta, todo su contenido quedará oculto allí sin modificar cada descendiente." : "El elemento volverá a aparecer entre los activos cuando su carpeta superior también esté activa."}</p></div>}
      {(action.type === "delete-folder" || action.type === "delete-file") && <div className="confirm-dialog full"><p>{action.type === "delete-folder" ? "Sólo se eliminará si la carpeta está vacía. Si contiene elementos, archívala o vacíala primero." : "La eliminación coordina Storage y metadata. Los archivos con dependencias deben archivarse o desvincularse primero."}</p></div>}
      <FormActions onCancel={() => setAction(null)} submitLabel={actionSubmitLabel(action)}/>
    </form>}</Modal>
  </div>;
}
